// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminOrganizaciones (migrado al shell · prompt 0471)
// CRUD de organizaciones (holdings, empresas, filiales, etc.)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  fetchOrganizaciones,
  crearOrganizacion,
  actualizarOrganizacion,
  eliminarOrganizacion,
  request,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { C } from '../../../../../lib/design';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

interface Org {
  id: string;
  codigo: string;
  nombre: string;
  nombreCorto: string | null;
  tipo: string;
  nif?: string;
  paisId?: string;
  organizacionPadreId?: string | null;
  participacionEnPadre?: number | null;
  activo: boolean;
  _count?: { departamentos: number; puestos: number };
}

function EditIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#DC2626"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function PowerIcon({ activo }: { activo: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={activo ? '#16A34A' : '#9CA3AF'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2v10" />
      <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />
    </svg>
  );
}

export function AdminOrganizaciones() {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modal, setModal] = useState<Org | 'new' | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchOrganizaciones()
      .then((r) => setOrgs((r.data ?? []) as unknown as Org[]))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleToggleActivo = async (o: Org) => {
    try {
      await actualizarOrganizacion(o.id, { activo: !o.activo });
      load();
      showToast(adm.saved ?? 'Guardado');
    } catch {
      showToast('Error');
    }
  };

  const handleBorrar = async (id: string) => {
    try {
      await eliminarOrganizacion(id);
      load();
      showToast(adm.saved ?? 'Eliminado');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Error');
    } finally {
      setConfirmId(null);
    }
  };

  const q = search.toLowerCase().trim();
  const filtered = orgs
    .filter((o) => (mostrarInactivos ? true : o.activo !== false))
    .filter((o) =>
      q
        ? (o.codigo ?? '').toLowerCase().includes(q) ||
          o.nombre.toLowerCase().includes(q) ||
          (o.nombreCorto ?? '').toLowerCase().includes(q) ||
          (o.nif ?? '').toLowerCase().includes(q) ||
          o.tipo.toLowerCase().includes(q)
        : true,
    );

  const tooltips = (adm.tooltips as Record<string, string> | undefined) ?? {};

  const columns: AdminTableColumn<Org>[] = [
    {
      key: 'codigo',
      label: adm.colCodigo ?? 'Código',
      sortable: true,
      sortKey: (o) => o.codigo ?? '',
      render: (o) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.azul2 }}>
          {o.codigo && o.codigo.length > 0 ? o.codigo : '—'}
        </span>
      ),
    },
    {
      key: 'nombre',
      label: adm.colNombre ?? adm.nombre ?? 'Nombre',
      sortable: true,
      sortKey: (o) => o.nombre,
      render: (o) => <span style={{ fontWeight: 600 }}>{o.nombre}</span>,
    },
    {
      key: 'nombreCorto',
      label: adm.nombreCorto ?? 'Nombre corto',
      sortable: true,
      sortKey: (o) => o.nombreCorto ?? '',
      render: (o) => <span style={{ color: '#64748B' }}>{o.nombreCorto ?? '—'}</span>,
    },
    {
      key: 'tipo',
      label: adm.tipo ?? 'Tipo',
      sortable: true,
      sortKey: (o) => o.tipo,
      render: (o) => <span style={{ color: '#64748B', fontSize: 12 }}>{o.tipo}</span>,
    },
    {
      key: 'nif',
      label: adm.nif ?? 'NIF',
      mono: true,
      sortable: true,
      sortKey: (o) => o.nif ?? '',
      render: (o) => <span>{o.nif ?? '—'}</span>,
    },
    {
      key: 'departamentos',
      label: adm.departamentos ?? 'Departamentos',
      align: 'center',
      sortable: true,
      sortKey: (o) => o._count?.departamentos ?? 0,
      render: (o) => <span>{o._count?.departamentos ?? 0}</span>,
    },
    {
      key: 'puestos',
      label: adm.puestos ?? 'Puestos',
      align: 'center',
      sortable: true,
      sortKey: (o) => o._count?.puestos ?? 0,
      render: (o) => <span>{o._count?.puestos ?? 0}</span>,
    },
    {
      key: 'activo',
      label: adm.activo ?? 'Activo',
      align: 'center',
      width: 90,
      render: (o) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: o.activo ? '#ECFDF5' : '#FEF2F2',
            color: o.activo ? '#16A34A' : '#DC2626',
          }}
        >
          {o.activo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={adm.organizations ?? adm.organizaciones ?? 'Organizaciones'}
        infoTooltip={{
          title: adm.organizations ?? 'Organizaciones',
          text: 'Entidades jurídicas que componen el grupo corporativo del tenant.\n\nCada organización puede tener un padre (jerarquía societaria) y un porcentaje de participación. Tipos soportados: Holding, Empresa, Filial, Sucursal, División, Unidad de Negocio, UTE, Joint Venture y Concesión.\n\nLos departamentos, puestos y centros se asocian a una organización concreta.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search ?? 'Buscar...',
        }}
        onNew={{
          label: adm.newOrganization ?? 'Nueva organizacion',
          onClick: () => setModal('new'),
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {adm.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(o) => o.id}
        onRowClick={(o) => setModal(o)}
        loading={loading}
        loadingMessage={((t.common as Record<string, unknown>)?.cargando as string) ?? 'Cargando…'}
        emptyMessage={adm.noResults ?? 'Sin organizaciones'}
        actions={(o) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label={tooltips.editar ?? 'Editar'}
              onClick={() => setModal(o)}
            />
            <AdminActionButton
              icon={<PowerIcon activo={o.activo} />}
              label={
                o.activo
                  ? (tooltips.toggleDesactivar ?? 'Desactivar')
                  : (tooltips.toggleActivar ?? 'Activar')
              }
              onClick={() => handleToggleActivo(o)}
            />
            <AdminActionButton
              icon={<TrashIcon />}
              label={tooltips.eliminar ?? 'Eliminar'}
              variant="danger"
              onClick={() => setConfirmId(o.id)}
            />
          </>
        )}
        footer={
          !loading ? (
            <span>
              {filtered.length} de {orgs.length} {adm.organizacionesContador ?? 'organizaciones'}
            </span>
          ) : undefined
        }
      />

      {modal !== null && (
        <OrgModal
          org={modal === 'new' ? null : modal}
          allOrgs={orgs}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
            showToast(adm.saved ?? 'Guardado');
          }}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title={tooltips.eliminar ?? 'Eliminar organización'}
        message={adm.confirmDelete ?? 'Eliminar esta organización?'}
        danger
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && handleBorrar(confirmId)}
      />
    </div>
  );
}

function OrgModal({
  org,
  onClose,
  onSaved,
  allOrgs,
}: {
  org: Org | null;
  onClose: () => void;
  onSaved: () => void;
  allOrgs: Org[];
}) {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const isEdit = org !== null;
  const initialForm: Record<string, string> = isEdit
    ? {
        nombre: org.nombre,
        nombreCorto: org.nombreCorto ?? '',
        tipo: org.tipo,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        nif: (org as any).nif ?? '',
        organizacionPadreId: org.organizacionPadreId ?? '',
        participacionEnPadre:
          org.participacionEnPadre != null ? String(org.participacionEnPadre) : '',
      }
    : { tipo: 'EMPRESA' };
  const [form, setForm] = useState<Record<string, string>>(initialForm);

  const parentOptions = allOrgs.filter((o) => {
    if (!isEdit) return true;
    if (o.id === org.id) return false;
    return true;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [paises, setPaises] = useState<Array<{ value: string; label: string }>>([]);

  useEffect(() => {
    request<{ data: Array<{ id: string; nombre: string }> }>('/paises')
      .then((r) => setPaises((r.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.nombre?.trim()) {
      setError('Nombre es obligatorio');
      return;
    }
    try {
      setSaving(true);
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v) body[k] = v;
      }
      if (body.participacionEnPadre) {
        body.participacionEnPadre = Number(body.participacionEnPadre);
      }
      if (!body.organizacionPadreId) {
        body.organizacionPadreId = null;
      }
      if (isEdit) await actualizarOrganizacion(org.id, body);
      else await crearOrganizacion(body);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };
  const inp: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    width: '100%',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: '#5A6171',
    marginBottom: 4,
    display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: '3px solid #1F3864',
          maxWidth: 600,
          width: '95%',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit ? 'Editar organizacion' : 'Nueva organizacion'}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div>
            <label style={lbl}>Nombre *</label>
            <input
              style={inp}
              value={form.nombre ?? ''}
              onChange={(e) => set('nombre', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Nombre corto</label>
            <input
              style={inp}
              value={form.nombreCorto ?? ''}
              onChange={(e) => set('nombreCorto', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.tipo ?? 'Tipo'}</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.tipo ?? ''}
              onChange={(e) => set('tipo', e.target.value)}
            >
              <option value="HOLDING">Holding</option>
              <option value="EMPRESA">Empresa</option>
              <option value="FILIAL">Filial</option>
              <option value="SUCURSAL">Sucursal</option>
              <option value="DIVISION">Division</option>
              <option value="UNIDAD_NEGOCIO">Unidad de Negocio</option>
              <option value="UTE">UTE</option>
              <option value="JOINT_VENTURE">Joint Venture</option>
              <option value="CONCESION">Concesion</option>
            </select>
          </div>
          <div>
            <label style={lbl}>{adm.nif ?? 'NIF'}</label>
            <input
              style={inp}
              value={form.nif ?? ''}
              onChange={(e) => set('nif', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Pais</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.paisId ?? ''}
              onChange={(e) => set('paisId', e.target.value)}
            >
              <option value="">--</option>
              {paises.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>Organizacion padre</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.organizacionPadreId ?? ''}
              onChange={(e) => set('organizacionPadreId', e.target.value)}
            >
              <option value="">-- Sin padre --</option>
              {parentOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nombre} ({o.tipo})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>% Participacion en padre</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.01}
              style={inp}
              value={form.participacionEnPadre ?? ''}
              onChange={(e) => set('participacionEnPadre', e.target.value)}
              placeholder="0 - 100"
            />
          </div>
        </div>
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginTop: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: '#2F5496',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
